import React, { useState } from 'react'
import { Draggable } from '@hello-pangea/dnd'
import { Card, CardContent, Typography, Chip, Box, Avatar, IconButton, Collapse } from '@mui/material'
import { ExpandMore, ErrorOutline } from '@mui/icons-material'
import { Ticket } from '@shared/types/entities'

interface KanbanTicketCardProps {
  ticket: Ticket
  index: number
  onClick: (ticketId: string) => void
  /** Render the card collapsed to its "spine" by default (number + subject). Lift it out to see detail. */
  defaultCollapsed?: boolean
}

const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'Urgent':
      return '#d32f2f'
    case 'High':
      return '#f57c00'
    case 'Medium':
      return '#fbc02d'
    case 'Low':
      return '#388e3c'
    default:
      return '#757575'
  }
}

const getInitials = (name?: string): string => {
  if (!name) return '?'
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export const KanbanTicketCard: React.FC<KanbanTicketCardProps> = ({
  ticket,
  index,
  onClick,
  defaultCollapsed = true
}) => {
  const [expanded, setExpanded] = useState(!defaultCollapsed)

  // "Lift the card out of the rack" — expand/collapse in place without opening the ticket.
  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation()
    setExpanded(prev => !prev)
  }

  // The notes a closed card would carry on its back — resolution notes, falling back to the description.
  const notes = ticket.resolutionNotes?.trim() || ticket.description?.trim()

  return (
    <Draggable draggableId={ticket.ticketId} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onClick(ticket.ticketId)}
          sx={{
            mb: 1,
            cursor: 'pointer',
            borderLeft: `4px solid ${getPriorityColor(ticket.priority)}`,
            backgroundColor: snapshot.isDragging ? 'action.hover' : 'background.paper',
            '&:hover': {
              backgroundColor: 'action.hover',
              boxShadow: 2
            },
            transition: 'all 0.2s ease'
          }}
        >
          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
            {/* Spine — the "top of the card" you always see in the rack */}
            <Box display="flex" alignItems="center" gap={0.75}>
              <Typography variant="caption" color="text.secondary" fontWeight="medium" sx={{ flexShrink: 0 }}>
                {ticket.ticketNumber}
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.4
                }}
              >
                {ticket.subject}
              </Typography>
              {ticket.slaBreached && !expanded && (
                <ErrorOutline color="error" sx={{ fontSize: 16, flexShrink: 0 }} titleAccess="SLA Breached" />
              )}
              <IconButton
                size="small"
                onClick={toggleExpanded}
                aria-label={expanded ? 'Collapse ticket' : 'Lift out ticket'}
                aria-expanded={expanded}
                sx={{
                  p: 0.25,
                  flexShrink: 0,
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              >
                <ExpandMore fontSize="small" />
              </IconButton>
            </Box>

            {/* Card detail — revealed when you lift it out */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box mt={1}>
                <Box display="flex" justifyContent="flex-end" mb={1}>
                  <Chip
                    label={ticket.priority}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      backgroundColor: getPriorityColor(ticket.priority),
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>

                {/* Notes snippet — what's written on the back of the card */}
                {notes && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="p"
                    sx={{
                      mb: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.4
                    }}
                  >
                    {notes}
                  </Typography>
                )}

                {/* Bottom Row: Category & Assigned Agent */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  {ticket.categoryName && (
                    <Chip
                      label={ticket.categoryName}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                  )}

                  {ticket.assignedAgentName && (
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: '0.7rem',
                        bgcolor: 'primary.main'
                      }}
                      title={ticket.assignedAgentName}
                    >
                      {getInitials(ticket.assignedAgentName)}
                    </Avatar>
                  )}
                </Box>

                {/* SLA Breach Indicator */}
                {ticket.slaBreached && (
                  <Box mt={1}>
                    <Chip
                      label="SLA Breached"
                      size="small"
                      color="error"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  </Box>
                )}
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      )}
    </Draggable>
  )
}
